import React from 'react'
import Headline from '@/components/FeedPostBack/components/FeedPostBackHeadline'
import Text from '@/components/FeedPostBack/components/FeedPostBackText'
import Cta from '@/components/FeedPostBack/components/FeedPostBackCta'
import Table from '@/components/FeedPostBack/components/FeedPostBackTable'
import { FeedPostNotification } from '@/feed/Feed.interface'
import { saveStatistic } from '@/api/api'
import { shimmerEffectSvg, toBase64 } from '@/lib/utils'
import CardRichMedia from '@/components/common/CardRichMedia'

// Stands in for next/image's blur placeholder.
const SHIMMER_BACKGROUND = `url("data:image/svg+xml;base64,${toBase64(
  shimmerEffectSvg(700, 475)
)}")`

function getRichMediaUrl(attrs: {
  provider: string
  richMediaUrl: string
  richMediaCompressedUrl: string
  customData: { type: string }
}): string {
  if (attrs.provider === 'giphy') {
    return attrs.richMediaUrl
  }
  if (attrs.provider === 'external' && attrs.customData.type !== 'mp4') {
    return attrs.richMediaUrl
  }
  return attrs.richMediaCompressedUrl || attrs.richMediaUrl
}

function FeedPostBack({
  feedPostBack,
  id,
  pageParams
}: {
  feedPostBack: FeedPostNotification['back']
  id: string
  pageParams: { sdkAppId: string; sdkAppKey: string; alias: string }
}) {
  if (!feedPostBack || !Array.isArray(feedPostBack)) {
    return null
  }

  const sortedBackParts = feedPostBack.sort((a, b) => a.position - b.position)

  const sendClickStat = async (buttonNumber: number) => {
    try {
      await saveStatistic({
        key: `${pageParams.sdkAppId}${pageParams.sdkAppKey}`,
        data: {
          campaignGuid: id,
          alias: pageParams.alias,
          key:
            buttonNumber === 1
              ? 'card_back_button_click_one'
              : 'card_back_button_click_two'
        },
        additionalHeaders: {
          source: 'native'
        }
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  return (
    <div className="prose pt-6">
      {sortedBackParts.map((part) => (
        <React.Fragment key={`${part.type}-${part.position}`}>
          {part.type === 'rich_media' && (
            <CardRichMedia
              provider={part.attrs[0].provider}
              richMediaUrl={getRichMediaUrl(part.attrs[0])}
              richMediaPosterUrl={part.attrs[0].richMediaPosterUrl}
              customData={part.attrs[0].customData}
              imageClassName="w-full ml-auto mr-auto"
            />
          )}
          {part.type === 'image' && (
            <img
              width={part.attrs[0].width}
              height={part.attrs[0].height}
              src={part.attrs[0].imageUrl}
              loading="lazy"
              style={{
                backgroundImage: SHIMMER_BACKGROUND,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat'
              }}
              alt={part.attrs[0].name}
              className={`${part.position === 0 ? '-mt-6' : ''} w-full ml-auto mr-auto`}
            />
          )}

          {part.type === 'headline' && (
            <Headline
              headline={part.attrs[0].text}
              className={`${part.position === 0 ? 'pl-6 pr-6' : 'mt-6 pl-6 pr-6'}`}
            />
          )}
          {part.type === 'text' && (
            <Text text={part.attrs[0].text} className="pl-6 pr-6 " />
          )}
          {part.type === 'table' && (
            <div className="pl-6 pr-6">
              <Table
                tableHeading={
                  sortedBackParts.find(
                    (partItem) => partItem.type === 'heading'
                  )?.attrs[0].text
                }
                rows={part.attrs[0].rows}
              />
            </div>
          )}
          {part.type === 'call_to_action' && (
            <Cta
              className="pl-6 pr-6"
              sendClickStat={sendClickStat}
              buttons={part.attrs}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

export default FeedPostBack
